import {
  Alert,
  Anchor,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  FocusTrap,
  Group,
  Modal,
  NumberInput,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  UnstyledButton,
} from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { Topic, TopicExampleModel } from '../../types/TopicModels';
import { useTopic } from '../../context/TopicContext';
import { primaryColor } from '../../constants/theme';
import { TopicExamplesModal } from './TopicExamplesModal';

const colourPresets = [
  '#5ECFB1',
  '#a78bfa',
  '#f59e0b',
  '#ef4444',
  '#3b82f6',
  '#10b981',
  '#f97316',
  '#8b5cf6',
  '#06b6d4',
];

const genericTopicTitle = 'Ask Me Anything';
const genericTopicDescription = 'A general topic where you can ask the creator anything.';
const CIRCLEBE_FEE_PERCENTAGE = 0.2;

interface TopicEditorModalProps {
  opened: boolean;
  onClose: () => void;
  editingTopic?: Topic | null;
  onSaved?: (topic: Topic) => void;
  onDeleted?: () => void;
}

export const TopicEditorModal = ({
  opened,
  onClose,
  editingTopic,
  onSaved,
  onDeleted,
}: TopicEditorModalProps) => {
  const { createTopic, updateTopic, deleteTopic, isSaving, error } = useTopic();
  const isEditMode = !!editingTopic;

  const [showExamples, setShowExamples] = useState(false);
  const [pendingExample, setPendingExample] = useState<TopicExampleModel | null>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [colour, setColour] = useState('#5ECFB1');
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [rate, setRate] = useState<number | undefined>(undefined);
  const [isFree, setIsFree] = useState(true);
  const [isGeneric, setIsGeneric] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [localError, setLocalError] = useState<string | undefined>();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  useEffect(() => {
    if (!opened) {
      setShowExamples(false);
    }
  }, [opened]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (editingTopic) {
      setTitle(editingTopic.isGeneric ? genericTopicTitle : editingTopic.title || '');
      setDescription(
        editingTopic.isGeneric
          ? editingTopic.description || genericTopicDescription
          : editingTopic.description || ''
      );
      setColour(editingTopic.colour || '#5ECFB1');
      setDurationMinutes(editingTopic.durationMinutes || 30);
      setRate(editingTopic.rate ?? 0);
      setIsFree((editingTopic.rate ?? 0) === 0);
      setIsGeneric(editingTopic.isGeneric);
      setIsActive(editingTopic.isActive);
      setLocalError(undefined);
      return;
    }

    setTitle('');
    setDescription('');
    setColour('#5ECFB1');
    setDurationMinutes(30);
    setRate(undefined);
    setIsFree(true);
    setIsGeneric(false);
    setIsActive(true);
    setLocalError(undefined);
  }, [editingTopic, opened]);

  useEffect(() => {
    if (pendingExample && !showExamples && opened) {
      setTitle(pendingExample.isGeneric ? genericTopicTitle : pendingExample.title);
      setDescription(
        pendingExample.isGeneric
          ? pendingExample.description || genericTopicDescription
          : pendingExample.description || ''
      );
      setDurationMinutes(pendingExample.suggestedDurationMinutes);
      setIsGeneric(pendingExample.isGeneric);
      setPendingExample(null);
    }
  }, [opened, pendingExample, showExamples]);

  const durationData = useMemo(
    () => ['15', '30', '45', '60'],
    []
  );

  const effectiveRate = useMemo(
    () => (isFree ? 0 : (rate ?? 0)),
    [isFree, rate]
  );

  const circlebeFee = useMemo(
    () => Number((effectiveRate * CIRCLEBE_FEE_PERCENTAGE).toFixed(2)),
    [effectiveRate]
  );

  const userPayout = useMemo(
    () => Number((effectiveRate - circlebeFee).toFixed(2)),
    [circlebeFee, effectiveRate]
  );

  const handleSave = async () => {
    setLocalError(undefined);

    if (!isGeneric && !title.trim()) {
      setLocalError('Please enter a topic title.');
      return;
    }

    const payload = {
      title: isGeneric ? genericTopicTitle : title.trim(),
      description: description.trim() || undefined,
      colour,
      ...(isActive ? { rate: isFree ? 0 : rate ?? 0 } : {}),
      durationMinutes,
      isGeneric,
    };

    try {
      if (isEditMode && editingTopic) {
        const updated = await updateTopic(editingTopic.id, {
          ...payload,
          isActive,
        });
        onSaved?.(updated);
      } else {
        const created = await createTopic(payload);
        onSaved?.(created);
      }

      onClose();
    } catch {
      // context handles error state
    }
  };

  const handleDelete = async () => {
    if (!editingTopic) {
      return;
    }

    try {
      await deleteTopic(editingTopic.id);
      setConfirmDeleteOpen(false);
      onDeleted?.();
      onClose();
    } catch {
      // context handles error state
    }
  };

  return (
    <>
      <Modal
        opened={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        withCloseButton={false}
        title="Delete Topic"
        centered
        size="sm"
        zIndex={300}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed" ta="left">
            Are you sure you want to delete this topic? This action cannot be undone.
          </Text>
          <Group grow>
            <Button color="red" onClick={handleDelete} loading={isSaving} data-testid="topic-confirm-delete-button">
              Delete
            </Button>
            <Button variant="outline" color={primaryColor} onClick={() => setConfirmDeleteOpen(false)} data-testid="topic-confirm-cancel-button">
              Cancel
            </Button>
          </Group>
        </Stack>
      </Modal>

      <TopicExamplesModal
        opened={showExamples}
        onClose={() => setShowExamples(false)}
        onSelect={(example) => {
          setPendingExample(example);
          setShowExamples(false);
        }}
      />

      <Modal
        opened={opened && !showExamples}
        onClose={onClose}
        withCloseButton={false}
        title={
          <Group justify="space-between" align="center" w="100%" pr="sm">
            <Text fw={600}>{isEditMode ? 'Edit Topic' : 'New Topic'}</Text>
            {isEditMode && (
              <Button
                color="red"
                variant="filled"
                size="xs"
                onClick={() => setConfirmDeleteOpen(true)}
                data-testid="topic-delete-button"
              >
                Delete
              </Button>
            )}
          </Group>
        }
        centered
        size="lg"
        styles={{ title: { width: '100%' } }}
      >
        <Stack gap="sm">
          <FocusTrap.InitialFocus />

          {(localError || error) && (
            <Alert color="red" variant="light" data-testid="topic-editor-error">
              {localError || error}
            </Alert>
          )}

          <Text fw={600} size="sm" c="dimmed" ta="left">
            Colour
          </Text>
          <Group gap={8}>
            {colourPresets.map((swatch) => (
              <button
                key={swatch}
                type="button"
                onClick={() => setColour(swatch)}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  border: colour === swatch ? '2px solid #111' : '1px solid #ddd',
                  backgroundColor: swatch,
                  cursor: 'pointer',
                }}
                aria-label={`Select ${swatch}`}
              />
            ))}
          </Group>          <Text size="sm" c="dimmed" ta="left">
            Not sure what to offer?{' '}
            <Anchor
              component="button"
              type="button"
              size="sm"
              c={primaryColor}
              data-testid="topic-browse-examples"
              onClick={() => setShowExamples(true)}
            >
              Browse examples
            </Anchor>
          </Text>          <Switch
            label="Ask Me Anything"
            description="Let followers ask about any topic"
            color={primaryColor}
            size="lg"
            labelPosition="left"
            data-testid="topic-ask-me-anything-switch"
            styles={{
              root: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
              body: { flex: 1, justifyContent: 'space-between', alignItems: 'center' },
              label: { fontSize: 'var(--mantine-font-size-md)', fontWeight: 600, flex: 1 },
              description: { marginTop: 0, fontSize: 'var(--mantine-font-size-xs)' },
            }}
            checked={isGeneric}
            onChange={(event) => {
              const checked = event.currentTarget.checked;
              setIsGeneric(checked);

              if (checked) {
                setTitle(genericTopicTitle);
                setDescription(genericTopicDescription);
                return;
              }

              setTitle('');
              setDescription('');
            }}
          />

          <TextInput
            label="Title"
            placeholder="e.g. Build My Workout Plan"
            value={title}
            data-testid="topic-title-input"
            onChange={(event) => setTitle(event.currentTarget.value)}
          />

          <Textarea
            label="Description"
            placeholder="Describe what the follower will get from this session"
            value={description}
            data-testid="topic-description-input"
            onChange={(event) => setDescription(event.currentTarget.value)}
            minRows={3}
          />

          <Stack gap={6}>
            <Text fw={500} size="sm" ta="left">
              Duration
            </Text>
            <Group gap={8}>
              {durationData.map((item) => {
                const isActive = String(durationMinutes) === item;
                return (
                  <UnstyledButton
                    key={item}
                    onClick={() => setDurationMinutes(Number(item))}
                    data-testid={`topic-duration-${item}`}
                    style={{
                      padding: '6px 14px',
                      borderRadius: 6,
                      border: `1px solid ${isActive ? primaryColor : 'var(--mantine-color-gray-4)'}`,
                      backgroundColor: isActive ? primaryColor : 'transparent',
                      color: isActive ? 'white' : 'var(--mantine-color-dark-6)',
                      fontSize: 'var(--mantine-font-size-sm)',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item} mins
                  </UnstyledButton>
                );
              })}
            </Group>
          </Stack>

          <Group align="end">
            <NumberInput
              label="Price"
              placeholder="e.g. 50"
              value={rate}
              disabled={isFree}
              min={0}
              onChange={(value) => setRate(Number(value) || 0)}
              style={{ flex: 1 }}
            />
            <Checkbox
              label="Free"
              color={primaryColor}
              checked={isFree}
              data-testid="topic-free-checkbox"
              onChange={(event) => {
                const checked = event.currentTarget.checked;
                setIsFree(checked);
                if (checked) {
                  setRate(0);
                }
              }}
              mb={6}
            />
          </Group>

          <Box
            style={{
              height: 1,
              backgroundColor: 'var(--mantine-color-gray-3)',
              marginTop: 'var(--mantine-spacing-sm)',
              marginBottom: 'var(--mantine-spacing-xs)',
            }}
          />

          <Flex
            justify="space-between"
            align="center"
            px="md"
            h={42}
            style={{
              borderRadius: 'var(--mantine-radius-sm)',
              border: '1px solid #93e9c0',
              backgroundColor: '#e8f9f0',
            }}
          >
            <Text size="md" fw={600} c="dark.6">
              You Get
            </Text>
            <Text size="lg" fw={700} c={primaryColor} data-testid="topic-you-get-amount">
              £{userPayout.toFixed(2)}
            </Text>
          </Flex>

          <Flex
            justify="space-between"
            align="center"
            px="md"
            h={42}
            style={{
              borderRadius: 'var(--mantine-radius-sm)',
              border: '1px solid var(--mantine-color-gray-3)',
              backgroundColor: 'var(--mantine-color-gray-0)',
            }}
          >
            <Text size="md" fw={600} c="dark.6">
              CircleBe Fee
            </Text>
            <Text size="lg" fw={700} c="dark.6" data-testid="topic-circlebe-fee-amount">
              £{circlebeFee.toFixed(2)}
            </Text>
          </Flex>

          <Text size="xs" c="dimmed" ta="center">
            You keep 80% of every booking while CircleBe handles secure booking, payments, and in-app video calls.
          </Text>

          {isEditMode && (
            <>
              <Divider />
              <Switch
                label="Pause this topic"
              description="Paused topics won't appear to followers"
              checked={!isActive}
              color={primaryColor}
              size="lg"
              labelPosition="left"
              data-testid="topic-pause-switch"
              styles={{
                root: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
                body: { flex: 1, justifyContent: 'space-between', alignItems: 'center' },
                label: { fontSize: 'var(--mantine-font-size-md)', fontWeight: 600, flex: 1 },
                description: { marginTop: 0, fontSize: 'var(--mantine-font-size-xs)' },
              }}
              onChange={(event) => setIsActive(!event.currentTarget.checked)}
            />
            </>
          )}

          <Group grow mt="md">
            <Button variant="default" onClick={onClose} data-testid="topic-cancel-button">
              Cancel
            </Button>
            <Button color={primaryColor} onClick={handleSave} loading={isSaving} data-testid="topic-save-button">
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
